import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ComicFilter from '@/components/feature/ComicFilter';
import ComicGrid from '@/components/base/ComicGrid';
import Pagination from '@/components/base/Pagination';
import { comics, hotComicIds } from '@/mocks/comics';
import {
  defaultFilter,
  filterComics,
  sortComics,
  sortFromQuery,
  type FilterState,
} from '@/utils/comic';

const PAGE_SIZE = 12;

export default function ListPage() {
  const [searchParams] = useSearchParams();
  const sortQuery = searchParams.get('sort');
  const [filter, setFilter] = useState<FilterState>({ ...defaultFilter, sort: sortFromQuery(sortQuery) });
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setFilter((prev) => ({ ...prev, sort: sortFromQuery(sortQuery) }));
    setPage(1);
  }, [sortQuery]);

  const results = useMemo(
    () => sortComics(filterComics(comics, { ...filter, keyword }), filter.sort),
    [filter, keyword],
  );

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const pageItems = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const updateFilter = (next: FilterState) => {
    setFilter(next);
    setPage(1);
  };

  const reset = () => {
    setFilter(defaultFilter);
    setKeyword('');
    setPage(1);
  };

  const hotComics = hotComicIds
    .map((id) => comics.find((comic) => comic.id === id))
    .filter(Boolean)
    .slice(0, 5);

  const activeCount =
    (filter.region !== '全部' ? 1 : 0) +
    (filter.status !== '全部' ? 1 : 0) +
    filter.categories.length +
    (keyword ? 1 : 0);

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8">
      <nav className="mb-4 flex items-center gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="transition hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">漫画列表</span>
      </nav>

      <header className="mb-6">
        <h1 className="font-heading text-2xl text-foreground-950 md:text-3xl">漫画库 · 多条件筛选</h1>
        <p className="mt-2 text-sm text-foreground-600">
          按题材、地区、状态与更新时间交叉筛选，快速定位你想看的漫画。
        </p>
      </header>

      <div className="mb-6 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
          <input
            value={keyword}
            onChange={(event) => {
              setKeyword(event.target.value);
              setPage(1);
            }}
            placeholder="输入关键词搜索漫画名称、作者或标签"
            className="w-full rounded-full border border-background-300 bg-background-50 py-2.5 pl-10 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-4 py-2.5 text-sm text-foreground-700 transition hover:border-primary-400 lg:hidden cursor-pointer"
        >
          <i className="ri-filter-3-line" />
          筛选{activeCount > 0 ? `（${activeCount}）` : ''}
        </button>
      </div>

      <div className="flex gap-8">
        <aside className="hidden w-[240px] shrink-0 lg:block">
          <div className="sticky top-20 rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="mb-1 flex items-center justify-between">
              <p className="text-sm font-medium text-foreground-900">筛选条件</p>
              <button
                type="button"
                onClick={reset}
                className="text-xs text-secondary-600 transition hover:text-secondary-700 cursor-pointer"
              >
                重置
              </button>
            </div>
            <ComicFilter value={filter} onChange={updateFilter} />
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <p className="text-sm text-foreground-600">
              共 <span className="font-medium text-primary-600">{results.length}</span> 部作品
            </p>
            {filter.region !== '全部' && (
              <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-900">
                地区：{filter.region}
              </span>
            )}
            {filter.status !== '全部' && (
              <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-900">
                状态：{filter.status}
              </span>
            )}
            {keyword && (
              <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-900">
                关键词：{keyword}
              </span>
            )}
          </div>

          {results.length > 0 ? (
            <>
              <ComicGrid comics={pageItems} />
              <Pagination current={page} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <div>
              <ComicGrid comics={[]} onReset={reset} />
              <div className="mt-8">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-xl text-foreground-950">
                  <i className="ri-fire-line text-primary-500" />
                  为你推荐热门漫画
                </h2>
                <ComicGrid comics={hotComics as typeof comics} />
              </div>
            </div>
          )}
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setDrawerOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-4">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-base font-medium text-foreground-900">筛选条件</p>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <ComicFilter value={filter} onChange={updateFilter} />
            <div className="sticky bottom-0 mt-4 flex gap-3 bg-background-50 pt-2">
              <button
                type="button"
                onClick={reset}
                className="flex-1 whitespace-nowrap rounded-full border border-background-300 py-2.5 text-sm text-foreground-700 cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex-1 whitespace-nowrap rounded-full bg-primary-500 py-2.5 text-sm font-medium text-background-50 cursor-pointer"
              >
                查看 {results.length} 部作品
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}